import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, X, ImageOff, Pencil, Upload, Filter } from "lucide-react";
import { api, formatApiError, uploadImage } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";

const FIELDS = [
  { name: "title", label: "Title / name", ph: "e.g. Winter rink night" },
  { name: "alt", label: "Alt text", required: true, ph: "Describe the image for accessibility" },
  { name: "caption", label: "Caption", ph: "Optional caption shown with the image" },
  { name: "credit", label: "Credit / photographer", ph: "Optional attribution" },
];

export default function MediaLibrary() {
  const { reload } = useCMS();
  const [items, setItems] = useState([]);
  const [usedUrls, setUsedUrls] = useState(new Set());
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/content/media"), api.get("/media/usage")])
      .then(([r, u]) => { setItems(r.data); setUsedUrls(new Set(u.data?.used_urls || [])); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const isUnused = (m) => !usedUrls.has(m.url);
  const unusedItems = items.filter(isUnused);
  const shown = showUnusedOnly ? unusedItems : items;

  const deleteUnused = async () => {
    if (!unusedItems.length) return;
    if (!window.confirm(`Delete ${unusedItems.length} unused image${unusedItems.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all(unusedItems.map((m) => api.delete(`/content/media/${m.id}`)));
      toast.success(`Deleted ${unusedItems.length} unused image${unusedItems.length === 1 ? "" : "s"}`);
      load(); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkDeleting(false); }
  };

  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set("url", url);
      if (!editing.alt) set("alt", file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing.url || !editing.alt) { toast.error("Image and alt text are required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/content/media/${editing.id}`, editing);
      else await api.post(`/content/media`, editing);
      toast.success("Saved"); setEditing(null); load(); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this image?")) return;
    try { await api.delete(`/content/media/${m.id}`); toast.success("Deleted"); load(); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]";

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl">Media Library</h1>
          <p className="mt-1 text-muted-foreground text-sm">Upload, edit and organise images. {unusedItems.length > 0 ? <span className="font-semibold text-[#DB1A5C]">{unusedItems.length} unused</span> : "Every image is in use."} · Every image needs alt text.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowUnusedOnly((v) => !v)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors cursor-pointer ${showUnusedOnly ? "bg-[#DB1A5C] text-white" : "bg-card border border-border hover:border-[#2CACD4]"}`} data-testid="media-filter-unused">
            <Filter size={16} /> {showUnusedOnly ? "Showing unused" : "Show unused only"}
          </button>
          {unusedItems.length > 0 && (
            <button onClick={deleteUnused} disabled={bulkDeleting} className="inline-flex items-center gap-2 rounded-full border border-[#DB1A5C]/40 text-[#DB1A5C] hover:bg-[#DB1A5C]/10 font-bold px-4 py-2.5 transition-colors cursor-pointer disabled:opacity-60" data-testid="media-delete-unused">
              {bulkDeleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />} Delete unused ({unusedItems.length})
            </button>
          )}
          <button onClick={() => setEditing({ url: "", title: "", alt: "", caption: "", credit: "", status: "published" })} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-5 py-2.5 transition-colors cursor-pointer" data-testid="media-add-btn">
            <Plus size={18} /> Add image
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
      ) : shown.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground"><ImageOff className="mx-auto mb-2" /> {showUnusedOnly ? "No unused images — everything is referenced on the site." : "No media yet."}</div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((m) => (
            <div key={m.id} className="group rounded-2xl border border-border bg-card overflow-hidden" data-testid="media-card">
              <div className="relative h-40">
                <img src={m.url} alt={m.alt} className="h-full w-full object-cover" />
                <div className="absolute top-2 left-2 flex gap-1.5">
                  {m.status === "draft" && <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground">draft</span>}
                  {isUnused(m) && <span className="rounded-full bg-[#DB1A5C] px-2.5 py-1 text-xs font-bold text-white" data-testid="media-unused-badge">Unused</span>}
                </div>
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(m)} className="rounded-full bg-white/90 p-2 text-[#0F172A] hover:bg-white cursor-pointer" aria-label="Edit" data-testid="media-edit"><Pencil size={15} /></button>
                  <button onClick={() => remove(m)} className="rounded-full bg-white/90 p-2 text-[#DB1A5C] hover:bg-white cursor-pointer" aria-label="Delete" data-testid="media-delete"><Trash2 size={15} /></button>
                </div>
              </div>
              <button onClick={() => setEditing(m)} className="block w-full p-3 text-left hover:bg-muted transition-colors cursor-pointer">
                <p className="text-sm font-semibold truncate">{m.title || m.alt}</p>
                {m.title && <p className="text-xs text-muted-foreground truncate">{m.alt}</p>}
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-3xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="media-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display font-black uppercase text-xl">{editing.id ? "Edit" : "Add"} image</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer"><X size={20} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5">Image <span className="text-[#DB1A5C]">*</span></label>
                {editing.url ? <img src={editing.url} alt="preview" className="mb-2 h-40 w-full rounded-xl object-cover border border-border" /> : <div className="mb-2 flex h-40 items-center justify-center rounded-xl border border-dashed border-border text-muted-foreground"><ImageOff /></div>}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-xs font-bold hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer disabled:opacity-60" data-testid="media-upload-btn">
                    {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {editing.url ? "Replace file" : "Upload file"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="media-upload-input" />
                </div>
                <input value={editing.url || ""} onChange={(e) => set("url", e.target.value)} className={`${inputCls} mt-2`} placeholder="…or paste an image URL" data-testid="media-url" />
              </div>
              {FIELDS.map((f) => (
                <div key={f.name}>
                  <label className="block text-sm font-semibold mb-1.5">{f.label}{f.required && <span className="text-[#DB1A5C]"> *</span>}</label>
                  <input value={editing[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls} placeholder={f.ph} data-testid={`media-${f.name}`} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-semibold mb-1.5">Status</label>
                <select value={editing.status || "published"} onChange={(e) => set("status", e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="media-status">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 font-bold hover:bg-muted cursor-pointer">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-60" data-testid="media-save">
                {saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
